import React from 'react'
import { useApp } from '../context/AppContext'
import { useOrgName } from '../shared/BrandLogo'
import { Tabs } from '../shared/components'
import SessionsScreen from './SessionsScreen'
import { StaffHub, ParticipantHub } from './TrainingHubScreen'
import CertBatchScreen from './CertBatchScreen'
import ClassesScreen from './ClassesScreen'

/* ============================================================
   Unified "Pelatihan" module — merges the former Sessions and
   Training Hub screens into one place with top-level tabs:
     Jadwal    → session management (CRUD, calendar, RSVP, QR)
     Live      → live operational control / participant live view
     Analitik  → program metrics, modules, activity (staff only)
   Entry deep-links still work: navigate('sessions') lands on
   Jadwal, navigate('training-hub') (or { tab:'live' }) on Live.
   ============================================================ */
export default function TrainingScreen() {
  const { lang, role, currentProgram, page, pageState } = useApp()
  const orgName = useOrgName()
  const isStaff = role === 'trainer' || role === 'admin'
  const isAdmin = role === 'admin'

  const resolveTab = React.useCallback(() => {
    if (pageState?.tab) return pageState.tab
    if (page === 'training-hub') return 'live'
    return 'schedule'
  }, [page, pageState])

  const [tab, setTab] = React.useState(resolveTab)
  // Re-sync when a deep-link navigates here again with a different intent.
  React.useEffect(() => { setTab(resolveTab()) }, [resolveTab])

  const tabs = [
    ...(isAdmin ? [{ id: 'classes', label: lang === 'id' ? 'Kelas' : 'Classes' }] : []),
    { id: 'schedule', label: lang === 'id' ? 'Jadwal' : 'Schedule' },
    { id: 'live', label: 'Live' },
    ...(isStaff ? [{ id: 'analytics', label: lang === 'id' ? 'Analitik' : 'Analytics' }] : []),
    ...(isStaff ? [{ id: 'credentials', label: lang === 'id' ? 'Kredensial' : 'Credentials' }] : []),
  ]

  // Participants have no analytics tab, non-admins have no classes tab —
  // fall back to schedule defensively.
  const activeTab = (!isStaff && tab === 'analytics') ? 'schedule'
    : (!isAdmin && tab === 'classes') ? 'schedule'
    : tab

  return (
    <div className="main-inner">
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">
            {(currentProgram?.code || orgName).toUpperCase()} · {lang === 'id' ? 'Pelatihan' : 'Training'}
          </div>
          <h1 className="h1" style={{ marginTop: 6 }}>{lang === 'id' ? 'Pelatihan' : 'Training'}</h1>
          <div className="muted mt-1">
            {lang === 'id'
              ? 'Jadwal sesi, kontrol live, dan analitik dalam satu tempat.'
              : 'Session schedule, live control, and analytics in one place.'}
          </div>
        </div>
      </div>

      <Tabs tabs={tabs} value={activeTab} onChange={setTab} />

      <div style={{ marginTop: 20 }}>
        {activeTab === 'classes' && isAdmin && <ClassesScreen embedded />}
        {activeTab === 'schedule' && <SessionsScreen embedded />}
        {activeTab === 'live' && (isStaff ? <StaffHub view="live" embedded /> : <ParticipantHub embedded />)}
        {activeTab === 'analytics' && isStaff && <StaffHub view="analytics" embedded />}
        {activeTab === 'credentials' && isStaff && <CertBatchScreen embedded />}
      </div>
    </div>
  )
}
